/**
 * @file Resolution des degats : critiques, application aux cibles shootable, explosions de zone, mort et recompenses.
 */
import { Decimal } from '@shared/bignum';
import { RESOURCES } from '@shared/enums';
import type { WalletId, WeaponBehavior } from '@shared/enums';
import type { EntityId } from '@shared/types';
import type { SimContext } from '../Context';
import type { Entity, With } from '../ecs/World';
import { dist } from '../math/geometry';
import type { CritRoll } from '../weapons/WeaponBehavior';
import type { ResolvedWeapon } from '../weapons/WeaponStats';

/** Cible valide pour l'auto-aim et les collisions : vivante, avec PV, shootable ET famille autorisee par le mode courant. */
export const isShootable = (ctx: SimContext, e: Entity): e is With<'target' | 'health' | 'collider'> => {
  if (!e.alive || !e.target || !e.health || !e.collider) return false;
  if (!e.target.shootable || !e.target.destructible) return false;
  if (e.visual && e.visual.dying > 0) return false;
  return ctx.modes.rules.targetableFamilies.includes(e.target.family);
};

/** Tirage de critique (flux rng.combat). */
export const rollCrit = (ctx: SimContext, w: ResolvedWeapon, base: Decimal): CritRoll => {
  const crit = ctx.rng.combat.next() < w.critChance;
  return { crit, damage: crit ? base.times(w.critDamage) : base };
};

/** Duree (s) de l'animation de mort avant destruction de l'entite. */
export const DYING_SECONDS = 0.45;

export interface DamageResult {
  killed: boolean;
}

/**
 * Applique `amount` a `target`.
 * Specification :
 *  1. Ignorer si !isShootable(ctx, target) ou health.invulnerable > 0 (retour { killed:false }).
 *  2. Si health.hitsLeft !== undefined (cible recoltable en mode 'resources') : hitsLeft -= 1 (un tir = un coup), sinon hp = hp.sub(amount).
 *  3. visual.hitFlash = 1 ; si mover : appliquer le knockback du mode (ModeManager.params knockback) dans mover.pushX/Y le long de (target - source) si fourni.
 *  4. Emettre 'EntityHit' { targetId, x, y, damage: amount, crit, behavior, killed }.
 *  5. Si hp <= 0 (ou hitsLeft <= 0) : killTarget(ctx, target) et retourner { killed:true }.
 */
export const applyDamage = (
  ctx: SimContext,
  target: With<'target' | 'health'>,
  amount: Decimal,
  crit: boolean,
  behavior: WeaponBehavior,
  sourceX?: number,
  sourceY?: number,
): DamageResult => {
  if (!isShootable(ctx, target) || target.health.invulnerable > 0) return { killed: false };
  const h = target.health;
  let dead: boolean;
  if (h.hitsLeft !== undefined) {
    h.hitsLeft -= 1;
    dead = h.hitsLeft <= 0;
  } else {
    h.hp = h.hp.sub(amount);
    ctx.modes.current.guardDamage?.(ctx, target);
    dead = h.hp.lte(0);
    if (dead) h.hp = new Decimal(0);
  }
  if (target.visual) target.visual.hitFlash = 1;
  const kb = ctx.modes.knockback;
  if (target.mover && kb > 0 && sourceX !== undefined && sourceY !== undefined) {
    const dx = target.transform.x - sourceX;
    const dy = target.transform.y - sourceY;
    const l = Math.hypot(dx, dy);
    if (l > 1e-6) {
      target.mover.pushX += (dx / l) * kb * 0.05;
      target.mover.pushY += (dy / l) * kb * 0.05;
    }
  }
  ctx.bus.emit('EntityHit', { targetId: target.id, x: target.transform.x, y: target.transform.y, damage: amount, crit, behavior, killed: dead });
  if (dead) {
    killTarget(ctx, target);
    return { killed: true };
  }
  return { killed: false };
};

/**
 * Mort d'une cible.
 * Specification :
 *  1. visual.dying = 0.45 s (animation de chute/eclatement cote rendu), retirer de ctx.rt.targetGrid (plus ciblable).
 *  2. Recompenses selon ctx.modes.rules.killRewards :
 *     - 'money'     : ctx.tx.earn({ money: reward.money }, 'kill') + drops dont `modes` inclut le mode courant (chance via rng.loot).
 *     - 'resources' : drops `flat` (ressources) x stat harvestYield via tx.earn ; pas d'argent.
 *     - 'score'     : ctx.modes.current.addScore(reward.score).
 *     - 'none'      : rien.
 *  3. ctx.data.progress.totalKills++ ; missions : 'treeKills' (family tree), 'monsterKills' (monster), 'bossKills' (isBoss), 'resourcesHarvested'.
 *  4. Emettre 'TargetKilled' { id, defId, family, x, y, money (credite), isBoss }.
 *  5. ctx.modes.current.onTargetKilled(ctx, target) (progression du niveau, vagues...).
 *  6. La destruction effective (world.destroy) est faite par StatusSystem quand visual.dying atteint 0.
 */
export const killTarget = (ctx: SimContext, target: With<'target' | 'health'>): void => {
  if (target.visual) target.visual.dying = DYING_SECONDS;
  ctx.rt.targetGrid.remove(target.id);
  const rewards = ctx.modes.rules.killRewards;
  const modeId = ctx.modes.currentId;
  let moneyCredited = new Decimal(0);
  let resources = 0;
  if (rewards === 'money' || rewards === 'resources') {
    const amounts: Partial<Record<WalletId, Decimal>> = {};
    if (rewards === 'money' && target.reward && target.reward.money.gt(0)) amounts.money = target.reward.money;
    for (const drop of target.reward?.drops ?? []) {
      if (drop.modes && !drop.modes.includes(modeId)) continue;
      if (rewards === 'resources' && drop.id === 'money') continue;
      if (drop.chance !== undefined && drop.chance < 1 && ctx.rng.loot.next() >= drop.chance) continue;
      const qty = Decimal.fromValue(drop.amount);
      amounts[drop.id] = (amounts[drop.id] ?? new Decimal(0)).plus(qty);
    }
    const credited = ctx.tx.earn(amounts, 'kill');
    moneyCredited = credited.money ?? moneyCredited;
    for (const id of RESOURCES) {
      const c = credited[id];
      if (c) resources += c.toNumber();
    }
  } else if (rewards === 'score') {
    ctx.modes.current.addScore(target.reward?.score ?? 0);
  }
  ctx.data.progress.totalKills++;
  const m = ctx.services.missions;
  if (target.target.family === 'tree') m.increment('treeKills', 1);
  if (target.target.family === 'monster') m.increment('monsterKills', 1);
  if (target.target.isBoss) m.increment('bossKills', 1);
  if (resources > 0) m.increment('resourcesHarvested', Math.round(resources));
  ctx.bus.emit('TargetKilled', {
    id: target.id,
    defId: target.target.defId,
    family: target.target.family,
    x: target.transform.x,
    y: target.transform.y,
    money: moneyCredited,
    isBoss: target.target.isBoss,
  });
  ctx.modes.current.onTargetKilled(ctx, target);
};

/**
 * Explosion de zone (explosive, missile, blastSniper, bomber).
 * Specification : ctx.rt.targetGrid.queryCircle(x, y, radius) ; pour chaque cible isShootable et non exclue :
 *  falloff lineaire de 1 (centre) a params.explosionFalloffAtEdge (bord) selon la distance ; applyDamage(...).
 *  Emettre 'Explosion' { x, y, radius, behavior } une seule fois. Retourner le nombre de cibles touchees.
 */
export const explode = (
  ctx: SimContext,
  x: number,
  y: number,
  radius: number,
  damage: Decimal,
  crit: boolean,
  behavior: WeaponBehavior,
  falloffAtEdge: number,
  exclude?: EntityId,
): number => {
  ctx.bus.emit('Explosion', { x, y, radius, behavior });
  const ids: EntityId[] = [];
  ctx.rt.targetGrid.queryCircle(x, y, radius, (id) => {
    if (id !== exclude) ids.push(id);
  });
  let hit = 0;
  for (const id of ids) {
    const e = ctx.rt.world.get(id);
    if (!e || !isShootable(ctx, e) || !e.health) continue;
    const t = radius > 0 ? Math.min(1, dist(x, y, e.transform.x, e.transform.y) / radius) : 1;
    const mult = 1 - (1 - falloffAtEdge) * t;
    applyDamage(ctx, e, damage.times(mult), crit, behavior, x, y);
    hit++;
  }
  return hit;
};

/** Degats d'une valeur nulle (utilitaire). */
export const ZERO_DAMAGE = new Decimal(0);
